/* eslint-disable */
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { CONSTANTS } from './config.service';
import { DateTimeService } from './date-time/date-time.service';
import { LoggerService } from './logger.service';
import { PersonsService } from './persons.service';
import { PrefsService } from './prefs.service';
import { UtilsService } from './utils.service';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';

@Injectable({
  providedIn: 'root'
})
export class StudyService {
  initialized = false;
  phases: any;
  studyStartYmd: any;
  studyStopYmd: any;
  studyStartSeconds: any;
  studyStopSeconds: any;
  studyUploadStopSeconds: any;

  constructor(
    private prefs: PrefsService,
    private logger: LoggerService,
    private dateTime: DateTimeService,
    private persons: PersonsService,
    private utils: UtilsService
  ) {
    this.logger.log('Study constructor', MSRemoteLoggerCategory.STUDY_SERVICE);
  }

  /************************************************************************************************************************************************************************
   * Study progress/status
   ***********************************************************************************************************************************************************************/

  hasBegun() {
    return moment().isAfter(moment(this.studyStartSeconds, 'X'));
  }

  isFirstDay() {
    return moment().format('YYYYMMDD') == this.studyStartYmd;
  }

  isAfterFirstStudyDay() {
    return moment().format('YYYYMMDD') > this.studyStartYmd;
  }

  isLastDay() {
    return moment().format('YYYYMMDD') == this.studyStopYmd;
  }

  isFinished() {
    this.logger.log('study.isFinished', MSRemoteLoggerCategory.STUDY_SERVICE);
    this.throwIfUninitd();
    return this.studyStopSeconds < this.dateTime.getNowSeconds();
  }

  isFinishedOverOneDay() {
    const secsPerDay = 86400;
    return this.studyStopSeconds + secsPerDay < this.dateTime.getNowSeconds();
  }

  isExpired() {
    this.throwIfUninitd();
    return this.getStudyExpirationSeconds().then((secs) => {
      const now = this.dateTime.getNowSeconds();
      return secs < now;
    });
  }

  getStudyExpirationSeconds() {
    return this.prefs
      .getPromise('studyExpirationDays')
      .then((days: number) => this.dateTime.getSeconds(days * 24, null, null) + this.studyStopSeconds)
      .catch(() => this.dateTime.getSeconds(CONSTANTS.STUDY_EXPIRATION_DAYS * 24, null, null) + this.studyStopSeconds);
  }

  /************************************************************************************************************************************************************************
   * Roster/calendar
   ***********************************************************************************************************************************************************************/

  ymdInStudyPeriod(targetYmd) {
    // not using studyStopSeconds since studyStopSeconds is technically midnight on day after last study day
    return this.studyStartYmd <= targetYmd && targetYmd <= this.studyStopYmd;
  }

  monthInStudyPeriod(targetMoment) {
    const start = parseInt(this.getStartMoment().format('YYYYMM'));
    const stop = parseInt(this.getStopMoment().format('YYYYMM'));
    const target = parseInt(targetMoment.format('YYYYMM'));
    return target >= start && target <= stop;

    // let startMonth = moment(this.studyStartSeconds, 'X').month();
    // let stopMonth = moment(this.studyStopSeconds, 'X').month();
    // return targetMonth >= startMonth && targetMonth <= stopMonth;
  }

  // used in tasklist/diary UI to get day-based info for all days within travel period
  getAllStudyDays() {
    const arr = [];
    const iMoment = this.getStartMoment();
    const stopMoment = this.getStopMoment();
    const weekdaysMin = moment.weekdaysMin(); // array of day abbreviations (e.g., ['S','M','T',...])

    while (iMoment.isSameOrBefore(stopMoment, 'day')) {
      arr.push({
        ymd: parseInt(iMoment.format('YYYYMMDD')),
        date: iMoment.format('lll'),
        fullDate: iMoment.format('llll'),
        weekdayMin: weekdaysMin[iMoment.day()],
        dayNum: iMoment.date(),
        moment: iMoment.clone(),
        phase: this.getPhaseByYMD(parseInt(iMoment.format('YYYYMMDD')))
      });
      iMoment.add(1, 'day');
    }

    return arr;
  }

  /************************************************************************************************************************************************************************
   * Phases
   ***********************************************************************************************************************************************************************/

  getPhaseByYMD(ymd) {
    let phase;

    ymd = parseInt(ymd, 10);

    this.phases.forEach((p) => {
      if (ymd >= p.start && ymd <= p.stop) {
        phase = p;
      }
    });

    return phase;
  }

  /************************************************************************************************************************************************************************
   * Survey function
   ***********************************************************************************************************************************************************************/

  getDayIndexFromYmd(YYYYMMDD) {
    const studyDiff = this.getStopMoment().diff(this.getStartMoment(), 'days');
    const dayDiff = moment(YYYYMMDD, 'YYYYMMDD').diff(this.getStartMoment(), 'days');
    const idx = Math.min(studyDiff, Math.max(0, dayDiff));

    // let startDay = parseInt(this.studyStartYmd), stopDay = parseInt(this.studyStopYmd), day = parseInt(YYYYMMDD);
    // let maxDayIdx = stopDay - startDay; // one day study max day is 0
    // let idx = Math.min(maxDayIdx, Math.max(0, day - startDay));
    // this.logger.log('Got day index in study for', idx, startDay, day, stopDay);
    return idx;
  }

  /************************************************************************************************************************************************************************
   * Proxy
   ***********************************************************************************************************************************************************************/

  getProxyDayForPhase(p) {
    return this.getAllStudyDays().filter((d) => d.ymd === p.proxyDay)[0];
  }

  /************************************************************************************************************************************************************************
   * Study params
   ***********************************************************************************************************************************************************************/

  throwIfUninitd() {
    if (!this.initialized) {
      throw 'Study service used before initialized';
    }
  }

  // TODO make these promises?
  getStartSeconds() {
    this.logger.log('study.getStartSeconds', MSRemoteLoggerCategory.STUDY_SERVICE);
    this.throwIfUninitd();
    return this.studyStartSeconds;
  }
  getStopSeconds() {
    return this.studyStopSeconds;
  }
  getUploadStopSeconds() {
    return this.studyUploadStopSeconds;
  }
  getStartYmd() {
    return this.studyStartYmd;
  }
  getStopYmd() {
    return this.studyStopYmd;
  }
  getStartMoment() {
    return moment(this.getStartYmd(), 'YYYYMMDD');
  }
  getStopMoment() {
    return moment(this.getStopYmd(), 'YYYYMMDD');
  }

  initialize() {
    return this.prefs
      .getPromises('startDate', 'stopDate', 'phases')
      .then((results) => {
        this.logger.log("init'ing study w/", MSRemoteLoggerCategory.STUDY_SERVICE, results);
        this.studyStartYmd = results[0];
        this.studyStopYmd = results[1];
        this.studyStartSeconds = moment(this.studyStartYmd, 'YYYYMMDD').unix();
        this.studyStopSeconds = moment(this.studyStopYmd, 'YYYYMMDD').add(1, 'day').unix(); // add one so end date is included (next midnight - overflow     works (Jan 32 -> Feb 1))
        this.studyUploadStopSeconds =
          this.studyStopSeconds + (CONSTANTS.STUDY_EXPIRATION_DAYS + CONSTANTS.AFTER_EXPIRATION_UPLOAD_DAYS) * 24 * 60 * 60; // ignoring daylight savings - close enough
        this.phases = results[2];
        this.initialized = true;
        return this.isExpired();
      })
      .then((expired) =>
        // WARN reason text hardcoded in initializer
        // return expired ? Promise.reject('Study expired') : Promise.resolve();
        Promise.resolve()
      );
  }

  // reset: function () {
  //     studyStartDate = null;
  //     studyStopDate = null;
  //     studyStartSeconds = null;
  //     studyStopSeconds = null;
  //     return $q.when();
  // },
}
